import {ReactElement} from 'react';
import {Navigate} from 'react-router-dom';
import {useAppSelector} from 'src/app/hooks';
import {resolveLandingRoute} from 'src/utils/resolveLandingRoute';

interface PermissionRouteProps {
    /**
     * What the page needs. Several means any one of them is enough.
     * <p>
     * A page can be reachable by more than one route: the statistics screen draws consumption, charging
     * sessions and two summaries, and different roles hold different halves of that. Naming one
     * permission for it meant picking whose half counted, and the page was gated on `reports.read` —
     * which it never calls.
     */
    permission: string | readonly string[];
    children: ReactElement;
}

/**
 * Renders a page only if the caller holds the permission, else sends them somewhere they can go.
 *
 * An element wrapper rather than route metadata: this project is on react-router 6.3, where
 * `RouteObject` has only caseSensitive/children/element/index/path. `handle` and `useMatches` arrived
 * in 6.4 with the data routers, and the app uses `<BrowserRouter>` + `useRoutes`. Upgrading the router
 * to express this declaratively is its own piece of work with its own risks.
 *
 * Before this there was no route guard at all — `routes.tsx` checked only that a localStorage key was
 * truthy, and a handful of pages called `navigate('/404')` from inside their own `useEffect`, which
 * renders the page first and redirects after.
 *
 * While permissions are still loading it renders nothing rather than redirecting. Redirecting on an
 * empty permission set would bounce every user off their own landing page on each refresh.
 */
export function PermissionRoute({permission, children}: PermissionRouteProps) {
    const access = useAppSelector((state) => state.common.access);

    if (!access) {
        return null;
    }
    const required = Array.isArray(permission) ? permission : [permission as string];
    if (!required.some((name) => access.permissions.includes(name))) {
        return <Navigate to={resolveLandingRoute(access)} replace/>;
    }
    return children;
}
